import { useState } from 'react';
import { Plus, Trash2, GripVertical, HelpCircle, X } from 'lucide-react';
import {
  useQualifyingQuestions,
  useCreateQualifyingQuestion,
  useUpdateQualifyingQuestion,
  useDeleteQualifyingQuestion,
} from '@/hooks/useQualifyingQuestions';
import { Button, Card, CardBody, CardHeader, EmptyState, Input, Modal, Select, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { QualifyingQuestion, QuestionImportance } from '@/types';

const importanceOptions = [
  { value: 'REQUIRED', label: 'Required' },
  { value: 'PREFERRED', label: 'Preferred' },
  { value: 'NICE_TO_HAVE', label: 'Nice to Have' },
];

const importanceBadge: Record<QuestionImportance, { variant: 'error' | 'warning' | 'default'; label: string }> = {
  REQUIRED: { variant: 'error', label: 'Required' },
  PREFERRED: { variant: 'warning', label: 'Preferred' },
  NICE_TO_HAVE: { variant: 'default', label: 'Nice to Have' },
};

export default function QualifyingQuestionSettings() {
  const { data: questions, isLoading } = useQualifyingQuestions();
  const [showAdd, setShowAdd] = useState(false);

  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Qualifying Questions"
          subtitle="Questions the AI agent uses to evaluate form submissions"
          actions={
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Question
            </Button>
          }
        />

        <CardBody className="space-y-2">
          {questions?.length === 0 ? (
            <EmptyState icon={<HelpCircle />} title="No qualifying questions configured" />
          ) : (
            questions?.map((question) => (
              <QuestionRow key={question.id} question={question} />
            ))
          )}
        </CardBody>
      </Card>

      {showAdd && (
        <AddQuestionDialog
          open={showAdd}
          onOpenChange={setShowAdd}
          nextPosition={questions?.length || 0}
        />
      )}
    </div>
  );
}

function KeywordTags({
  keywords,
  variant,
  onRemove,
}: {
  keywords: string[];
  variant: 'success' | 'error';
  onRemove?: (kw: string) => void;
}) {
  if (!keywords.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {keywords.map((kw) => (
        <Badge key={kw} variant={variant} size="sm" className="flex items-center gap-0.5">
          {kw}
          {onRemove && (
            <button type="button" onClick={() => onRemove(kw)} className="ml-0.5 hover:opacity-70" aria-label={`Remove ${kw}`}>
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </Badge>
      ))}
    </div>
  );
}

function KeywordInput({
  label,
  keywords,
  onChange,
  variant,
  placeholder,
}: {
  label: string;
  keywords: string[];
  onChange: (kws: string[]) => void;
  variant: 'success' | 'error';
  placeholder: string;
}) {
  const [value, setValue] = useState('');

  const add = () => {
    const trimmed = value.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onChange([...keywords, trimmed]);
      setValue('');
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-grey-500 mb-1 block">{label}</label>
      <KeywordTags keywords={keywords} variant={variant} onRemove={(kw) => onChange(keywords.filter((k) => k !== kw))} />
      <div className="flex gap-1 mt-1">
        <input
          className="input py-1 px-2 text-xs flex-1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
        />
        <button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-grey-100 hover:bg-grey-200 text-grey-600" aria-label={`Add ${label.toLowerCase()}`}>
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function QuestionRow({ question }: { question: QualifyingQuestion }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(question.question);
  const [importance, setImportance] = useState(question.importance);
  const [helpText, setHelpText] = useState(question.helpText || '');
  const [formField, setFormField] = useState(question.formField || '');
  const [redFlags, setRedFlags] = useState<string[]>(question.redFlagKeywords || []);
  const [greenFlags, setGreenFlags] = useState<string[]>(question.greenFlagKeywords || []);
  const updateQuestion = useUpdateQualifyingQuestion();
  const deleteQuestion = useDeleteQualifyingQuestion();

  const badge = importanceBadge[question.importance];

  const handleSave = async () => {
    await updateQuestion.mutateAsync({
      id: question.id,
      data: {
        question: text,
        importance,
        helpText: helpText || undefined,
        formField: formField || undefined,
        redFlagKeywords: redFlags,
        greenFlagKeywords: greenFlags,
      },
    });
    setEditing(false);
  };

  const handleDelete = async () => {
    if (!confirm(`Delete this qualifying question?`)) return;
    await deleteQuestion.mutateAsync(question.id);
  };

  const hasFlags = (question.redFlagKeywords?.length || 0) > 0 || (question.greenFlagKeywords?.length || 0) > 0;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-grey-50 group">
      <GripVertical className="w-4 h-4 text-grey-300 cursor-grab mt-1 flex-shrink-0" />

      <div className="text-sm text-grey-500 font-mono w-6 mt-0.5 flex-shrink-0">
        {question.position + 1}.
      </div>

      {editing ? (
        <div className="flex-1 space-y-2">
          <input
            className="input py-1 px-2 text-sm w-full"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Question text"
          />
          <div className="flex items-center gap-2">
            <Select
              aria-label="Importance"
              selectSize="sm"
              options={importanceOptions}
              value={importance}
              onChange={(e) => setImportance(e.target.value as QuestionImportance)}
            />
            <input
              className="input py-1 px-2 text-sm flex-1"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              placeholder="Help text (optional)"
            />
            <input
              className="input py-1 px-2 text-sm flex-1"
              value={formField}
              onChange={(e) => setFormField(e.target.value)}
              placeholder="Form field (optional)"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <KeywordInput
              label="Green Flag Keywords"
              keywords={greenFlags}
              onChange={setGreenFlags}
              variant="success"
              placeholder="e.g. approved budget"
            />
            <KeywordInput
              label="Red Flag Keywords"
              keywords={redFlags}
              onChange={setRedFlags}
              variant="error"
              placeholder="e.g. just exploring"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSave} loading={updateQuestion.isPending}>
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-secondary">{question.question}</p>
            {question.helpText && (
              <p className="text-xs text-grey-400 mt-0.5">{question.helpText}</p>
            )}
            {question.formField && (
              <p className="text-xs text-grey-400 mt-0.5 font-mono">Field: {question.formField}</p>
            )}
            {hasFlags && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                <KeywordTags keywords={question.greenFlagKeywords || []} variant="success" />
                <KeywordTags keywords={question.redFlagKeywords || []} variant="error" />
              </div>
            )}
          </div>

          <Badge variant={badge.variant} size="sm">
            {badge.label}
          </Badge>

          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-grey-600"
            >
              <span className="text-xs">Edit</span>
            </button>
            <button
              onClick={handleDelete}
              className="p-1 rounded hover:bg-grey-200 text-grey-400 hover:text-error"
              aria-label="Delete question"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AddQuestionDialog({
  open,
  onOpenChange,
  nextPosition,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextPosition: number;
}) {
  const [form, setForm] = useState({
    question: '',
    importance: 'PREFERRED' as QuestionImportance,
    helpText: '',
    formField: '',
    redFlagKeywords: [] as string[],
    greenFlagKeywords: [] as string[],
  });
  const createQuestion = useCreateQualifyingQuestion();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createQuestion.mutateAsync({
      question: form.question,
      importance: form.importance,
      helpText: form.helpText || undefined,
      formField: form.formField || undefined,
      position: nextPosition,
      redFlagKeywords: form.redFlagKeywords.length ? form.redFlagKeywords : undefined,
      greenFlagKeywords: form.greenFlagKeywords.length ? form.greenFlagKeywords : undefined,
    });
    onOpenChange(false);
    setForm({ question: '', importance: 'PREFERRED', helpText: '', formField: '', redFlagKeywords: [], greenFlagKeywords: [] });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Qualifying Question"
      description="Add a question for the AI agent to evaluate."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-question-form" loading={createQuestion.isPending}>
            Add Question
          </Button>
        </>
      }
    >
      <form id="add-question-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Question"
          required
          placeholder="e.g. What is your estimated budget?"
          value={form.question}
          onChange={(e) => setForm({ ...form, question: e.target.value })}
        />
        <Select
          label="Importance"
          options={importanceOptions}
          value={form.importance}
          onChange={(e) =>
            setForm({ ...form, importance: e.target.value as QuestionImportance })
          }
        />
        <Input
          label="Help Text (optional)"
          placeholder="Guidance for the AI agent"
          value={form.helpText}
          onChange={(e) => setForm({ ...form, helpText: e.target.value })}
        />
        <Input
          label="Form Field (optional)"
          placeholder="Maps to a form field name"
          value={form.formField}
          onChange={(e) => setForm({ ...form, formField: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <KeywordInput
            label="Green Flag Keywords"
            keywords={form.greenFlagKeywords}
            onChange={(kws) => setForm({ ...form, greenFlagKeywords: kws })}
            variant="success"
            placeholder="e.g. approved budget"
          />
          <KeywordInput
            label="Red Flag Keywords"
            keywords={form.redFlagKeywords}
            onChange={(kws) => setForm({ ...form, redFlagKeywords: kws })}
            variant="error"
            placeholder="e.g. just exploring"
          />
        </div>
      </form>
    </Modal>
  );
}
